import { useEffect, useState } from 'react';
import { useForm } from 'react-hook-form';
import { zodResolver } from '@hookform/resolvers/zod';
import { z } from 'zod';
import {
  AlertCircle,
  ArrowLeft,
  ArrowRight,
  CheckCircle2,
  Eye,
  EyeOff,
  Loader2,
  Lock,
  LockKeyhole,
  Mail,
  ShieldCheck,
} from 'lucide-react';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { BrandLogo } from '@/components/BrandMark';
import { AuthBackdrop } from '@/features/auth/AuthBackdrop';
import { useAuth } from '@/lib/auth';
import { describeError } from '@/lib/apiError';
import type { StaffChallenge } from '@/types/domain';
import { cn } from '@/lib/cn';

// -----------------------------------------------------------------------------
// Staff sign-in.
//
// TWO STEPS: an identifier and a password, then a six-digit code. There is no
// sign-up route - staff accounts are provisioned by a super admin, and a
// self-service path into an admin panel is a path an attacker can walk too.
//
// THE IDENTIFIER IS ONE FIELD, not a chooser. An operator types their work email
// or their mobile number and the server works out which; asking them to first
// declare what kind of thing they are about to type is a question with no
// purpose. The number only works once a super admin has set it on the account,
// which is why the hint says so rather than leaving a silent failure.
//
// THE CODE ALWAYS GOES TO EMAIL, even when the operator signed in by phone. SMS
// is not a channel on this platform, and the screen names the inbox rather than
// letting somebody wait for a text that is not coming.
//
// NOTHING IS SIGNED IN BETWEEN THE TWO STEPS. The password is verified on the
// server and no Firebase session exists until the code is accepted - so closing
// this tab at the code screen leaves nothing behind. See the header of
// `lib/auth.tsx`.
//
// LAYOUT - the mobile app's sign-in screen, made responsive. Same drifting photo
// wall, same brand lockup over it, same violet uppercase headline, same white
// form beneath. On a phone it is that screen; from `lg` the column moves to the
// right third and the wall keeps the rest, because a 1440px browser filled with
// a 400px centred column is a phone screenshot stretched over a monitor.
// -----------------------------------------------------------------------------

const signInSchema = z.object({
  // No `.email()` here - a mobile number is equally valid and the server does
  // the resolving. Validating for an email shape would reject the other half of
  // the feature before the request was even made.
  identifier: z.string().trim().min(3, 'Enter your work email or mobile number.'),
  password: z.string().min(1, 'Enter your password.'),
});

type SignInValues = z.infer<typeof signInSchema>;

const codeSchema = z.object({
  code: z
    .string()
    .trim()
    .regex(/^\d{6}$/, 'The code is six digits.'),
});

type CodeValues = z.infer<typeof codeSchema>;

const resetSchema = z.object({
  email: z.string().min(1, 'Enter your work email.').email('That is not a valid email address.'),
});

type ResetValues = z.infer<typeof resetSchema>;

/**
 * What the button says while the post-code chain runs.
 *
 * Sign-in does not finish when the credential resolves - the role claim, the
 * session POST and `/auth/me` all follow. The last step is open-ended, so a slow
 * network never shows a step that has already passed.
 */
const PROGRESS_STEPS = [
  'Verifying your credentials…',
  'Checking your access level…',
  'Opening the console…',
] as const;

export function LoginPage(): JSX.Element {
  const [mode, setMode] = useState<'sign-in' | 'reset'>('sign-in');

  return (
    <div className="relative min-h-[100dvh] overflow-hidden bg-background">
      <AuthBackdrop />

      <div className="relative flex min-h-[100dvh] flex-col lg:flex-row lg:justify-end">
        {/* The wall's own half at `lg`. Empty by design: it exists to push the
            column right, and the backdrop behind it is the content. */}
        <div className="hidden lg:block lg:flex-1" aria-hidden />

        <main className="relative flex w-full flex-col px-5 pb-8 pt-[20vh] sm:px-8 sm:pt-[22vh] lg:w-[min(560px,46vw)] lg:shrink-0 lg:justify-center lg:px-12 lg:py-10 lg:pt-10 xl:px-16">
          <div className="mx-auto flex w-full max-w-[400px] flex-col">
            {/* The brand lockup at the app's proportions: a glowing rounded tile
                with the wordmark beneath it. */}
            <div className="flex flex-col items-center gap-1">
              <BrandLogo size="xl" className="shadow-[0_0_20px_rgba(0,0,0,0.45)] ring-0" />
              <p className="text-sm font-bold uppercase tracking-[0.9px] text-[#4319BD]">
                SECT.FIT
              </p>
            </div>

            {/* The app's two-line headline: same violet, same weight, uppercase. */}
            <div className="mt-6 text-center">
              <h1 className="text-[28px] font-black uppercase leading-[1.15] tracking-[0.4px] text-[#4A00CC] sm:text-[34px]">
                {mode === 'sign-in' ? 'Welcome !' : 'Reset access'}
              </h1>
              <p className="mt-2 text-[15px] font-extrabold uppercase leading-tight tracking-[0.3px] text-[#4A00CC] sm:text-lg">
                {mode === 'sign-in' ? 'Admin console' : 'Staff accounts only'}
              </p>
            </div>

            {/* On a phone the form is flush with the white the wall dissolves
                into, exactly as the app's is. From `sm` it lifts onto its own
                surface so it stays readable over the livelier part of the wall
                on a wide screen. */}
            <div className="mt-7 sm:mt-8 sm:rounded-2xl sm:border sm:border-[rgba(220,220,220,0.8)] sm:bg-white/95 sm:p-7 sm:shadow-[0_4px_16px_-2px_rgba(0,0,0,0.10)] sm:backdrop-blur-sm">
              {mode === 'sign-in' ? (
                <SignInForm onForgotPassword={() => setMode('reset')} />
              ) : (
                <ResetForm onBack={() => setMode('sign-in')} />
              )}
            </div>

            <p className="mt-7 text-center text-xs leading-relaxed text-[#6C6C6C]">
              © {new Date().getFullYear()} SECTFIT HEALTHTECH PRIVATE LIMITED
              <br />
              Internal tool, restricted to staff.
            </p>
          </div>
        </main>
      </div>
    </div>
  );
}

function SignInForm({ onForgotPassword }: { onForgotPassword: () => void }): JSX.Element {
  const { startSignIn, error: sessionError, clearError, isVerifying } = useAuth();
  const [showPassword, setShowPassword] = useState(false);
  const [step, setStep] = useState(0);
  const [failure, setFailure] = useState<string | null>(null);

  // Present exactly when the password has been accepted and a code is waiting.
  const [challenge, setChallenge] = useState<StaffChallenge | null>(null);

  const {
    register,
    handleSubmit,
    formState: { errors, isSubmitting },
  } = useForm<SignInValues>({
    resolver: zodResolver(signInSchema),
    defaultValues: { identifier: '', password: '' },
  });

  const busy = isSubmitting || isVerifying;

  useEffect(() => {
    if (!busy) {
      setStep(0);
      return;
    }

    const timers = [setTimeout(() => setStep(1), 1400), setTimeout(() => setStep(2), 3200)];
    return () => timers.forEach(clearTimeout);
  }, [busy]);

  const onSubmit = handleSubmit(async (values) => {
    clearError();
    setFailure(null);
    try {
      setChallenge(await startSignIn(values.identifier, values.password));
    } catch (submitError) {
      setFailure(describeError(submitError).description);
    }
  });

  if (challenge) {
    return (
      <CodeForm
        challenge={challenge}
        onChallenge={setChallenge}
        onBack={() => {
          setChallenge(null);
          setFailure(null);
          clearError();
        }}
      />
    );
  }

  return (
    <>
      {sessionError || failure ? (
        <div
          role="alert"
          className="mb-5 flex items-start gap-3 rounded-lg border border-destructive/25 bg-danger-soft p-3.5"
        >
          <AlertCircle className="mt-0.5 size-4 shrink-0 text-destructive" aria-hidden />
          <div className="min-w-0">
            <p className="text-sm font-semibold leading-tight text-destructive">
              Could not sign you in
            </p>
            <p className="mt-1 text-sm leading-relaxed text-destructive/90">
              {sessionError ?? failure}
            </p>
          </div>
        </div>
      ) : null}

      <form onSubmit={onSubmit} noValidate className="flex flex-col gap-4">
        <Field
          id="identifier"
          label="Work email or mobile"
          icon={<Mail className="size-4" aria-hidden />}
          error={errors.identifier?.message}
        >
          <Input
            id="identifier"
            type="text"
            inputMode="email"
            autoComplete="username"
            autoFocus
            placeholder="you@sect.fit"
            disabled={busy}
            aria-invalid={Boolean(errors.identifier)}
            aria-describedby="identifier-hint"
            className="h-12 rounded-xl border-[rgba(220,220,220,0.8)] pl-9 shadow-[0_4px_6px_-1px_rgba(0,0,0,0.06)]"
            {...register('identifier')}
          />
        </Field>

        <p id="identifier-hint" className="-mt-2 text-xs leading-snug text-[#6C6C6C]">
          A mobile number works once a super admin has added it to your account.
        </p>

        <Field
          id="password"
          label="Password"
          icon={<Lock className="size-4" aria-hidden />}
          error={errors.password?.message}
          action={
            <button
              type="button"
              onClick={onForgotPassword}
              className="rounded text-sm font-medium text-primary transition-colors hover:text-brand-deep hover:underline focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring focus-visible:ring-offset-2"
            >
              Forgot password?
            </button>
          }
        >
          <Input
            id="password"
            type={showPassword ? 'text' : 'password'}
            autoComplete="current-password"
            // No dotted placeholder: in a password field it is indistinguishable
            // from a value already typed.
            disabled={busy}
            aria-invalid={Boolean(errors.password)}
            aria-describedby={errors.password ? 'password-error' : undefined}
            className="h-12 rounded-xl border-[rgba(220,220,220,0.8)] pl-9 pr-10 shadow-[0_4px_6px_-1px_rgba(0,0,0,0.06)]"
            {...register('password')}
          />
          <button
            type="button"
            onClick={() => setShowPassword((current) => !current)}
            aria-label={showPassword ? 'Hide password' : 'Show password'}
            disabled={busy}
            className={cn(
              'absolute right-2 top-1/2 -translate-y-1/2 rounded p-1.5 text-muted-foreground transition-colors',
              'hover:bg-muted hover:text-foreground',
              'focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring',
              'disabled:pointer-events-none disabled:opacity-50',
            )}
          >
            {showPassword ? (
              <EyeOff className="size-4" aria-hidden />
            ) : (
              <Eye className="size-4" aria-hidden />
            )}
          </button>
        </Field>

        <CtaButton
          busy={busy}
          label={busy ? (PROGRESS_STEPS[step] ?? PROGRESS_STEPS[0]) : 'Continue'}
        />

        {/* Announced without stealing focus, so the step is reported to a screen
            reader as it changes rather than only shown. */}
        <p aria-live="polite" className="sr-only">
          {busy ? (PROGRESS_STEPS[step] ?? PROGRESS_STEPS[0]) : ''}
        </p>
      </form>

      <p className="mt-6 flex items-start gap-2 text-xs leading-relaxed text-[#6C6C6C]">
        <ShieldCheck className="mt-0.5 size-3.5" aria-hidden />
        Every sign-in needs a one-time code from your email. Accounts are created by a super
        admin - there is no self-service sign-up.
      </p>
    </>
  );
}

/**
 * Step two - the one-time code.
 *
 * Its own component so the password fields UNMOUNT rather than hide. A hidden
 * input still holds its value, is still in the DOM, and is still offered by the
 * browser's autofill on the next screen; unmounting is what makes "the password
 * is no longer on this page" true rather than merely apparent.
 *
 * The countdown is derived from the server's `expiresAt` on every tick rather
 * than counted down locally: a laptop that slept for six minutes would otherwise
 * show four minutes left on a code that has already died.
 */
function CodeForm({
  challenge,
  onChallenge,
  onBack,
}: {
  challenge: StaffChallenge;
  onChallenge: (next: StaffChallenge) => void;
  onBack: () => void;
}): JSX.Element {
  const { completeSignIn, resendCode, isVerifying } = useAuth();
  const [failure, setFailure] = useState<string | null>(null);
  const [notice, setNotice] = useState<string | null>(null);
  const [now, setNow] = useState(() => Date.now());
  const [resendAt, setResendAt] = useState(
    () => Date.now() + challenge.resendAvailableInSeconds * 1000,
  );

  const {
    register,
    handleSubmit,
    setFocus,
    reset,
    formState: { errors, isSubmitting },
  } = useForm<CodeValues>({
    resolver: zodResolver(codeSchema),
    defaultValues: { code: '' },
  });

  useEffect(() => {
    setFocus('code');
  }, [setFocus]);

  useEffect(() => {
    const timer = setInterval(() => setNow(Date.now()), 1000);
    return () => clearInterval(timer);
  }, []);

  const msLeft = Math.max(Date.parse(challenge.expiresAt) - now, 0);
  const expired = msLeft === 0;
  const resendIn = Math.max(Math.ceil((resendAt - now) / 1000), 0);
  const busy = isSubmitting || isVerifying;

  const onSubmit = handleSubmit(async (values) => {
    setFailure(null);
    setNotice(null);
    try {
      await completeSignIn(challenge.challengeId, values.code);
      // No navigation here. `RedirectIfAuthenticated` moves the user once the
      // session is verified as staff - navigating on the credential alone would
      // flash the console for an account about to be rejected.
    } catch (submitError) {
      setFailure(describeError(submitError).description);
      // The wrong code is cleared and refocused. Making somebody select and
      // delete six digits before their next attempt is friction aimed at the
      // wrong person.
      reset({ code: '' });
      setFocus('code');
    }
  });

  const onResend = async (): Promise<void> => {
    setFailure(null);
    try {
      const next = await resendCode(challenge.challengeId);
      onChallenge(next);
      setResendAt(Date.now() + next.resendAvailableInSeconds * 1000);
      setNotice(`A new code is on its way to ${next.sentTo}.`);
    } catch (resendError) {
      setFailure(describeError(resendError).description);
    }
  };

  return (
    <>
      <button
        type="button"
        onClick={onBack}
        className="mb-4 inline-flex items-center gap-1.5 rounded text-sm font-medium text-muted-foreground transition-colors hover:text-foreground focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring"
      >
        <ArrowLeft className="size-4" aria-hidden />
        Back
      </button>

      <p className="mb-5 text-sm leading-relaxed text-[#434343]">
        We emailed a six-digit code to{' '}
        <strong className="font-semibold text-foreground">{challenge.sentTo}</strong>. It is
        needed even if you signed in with your mobile number.
      </p>

      {failure ? (
        <div
          role="alert"
          className="mb-5 flex items-start gap-3 rounded-lg border border-destructive/25 bg-danger-soft p-3.5"
        >
          <AlertCircle className="mt-0.5 size-4 shrink-0 text-destructive" aria-hidden />
          <div className="min-w-0">
            <p className="text-sm font-semibold leading-tight text-destructive">
              That did not work
            </p>
            <p className="mt-1 text-sm leading-relaxed text-destructive/90">{failure}</p>
          </div>
        </div>
      ) : notice ? (
        <div className="mb-5 flex items-start gap-3 rounded-lg border border-success/25 bg-success-soft p-3.5">
          <CheckCircle2 className="mt-0.5 size-4 shrink-0 text-success" aria-hidden />
          <p className="text-sm leading-relaxed text-success">{notice}</p>
        </div>
      ) : null}

      <form onSubmit={onSubmit} noValidate className="flex flex-col gap-4">
        <Field
          id="code"
          label="Six-digit code"
          icon={<LockKeyhole className="size-4" aria-hidden />}
          error={errors.code?.message}
        >
          <Input
            id="code"
            type="text"
            inputMode="numeric"
            // `one-time-code` is what makes iOS and Android offer the code from
            // the notification, and Chrome offer it from a linked phone.
            autoComplete="one-time-code"
            maxLength={6}
            placeholder="000000"
            disabled={busy || expired}
            aria-invalid={Boolean(errors.code)}
            className="h-12 rounded-xl border-[rgba(220,220,220,0.8)] pl-9 text-center font-mono text-lg tracking-[0.5em] shadow-[0_4px_6px_-1px_rgba(0,0,0,0.06)]"
            {...register('code')}
          />
        </Field>

        <div className="flex flex-wrap items-center justify-between gap-2 text-sm">
          <p className={cn('tabular', expired ? 'text-destructive' : 'text-[#6C6C6C]')}>
            {expired
              ? 'This code has expired.'
              : `Expires in ${Math.floor(msLeft / 60_000)}:${String(
                  Math.floor((msLeft % 60_000) / 1000),
                ).padStart(2, '0')}`}
          </p>

          <button
            type="button"
            onClick={() => void onResend()}
            disabled={resendIn > 0}
            className={cn(
              'rounded font-medium transition-colors focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring',
              resendIn > 0
                ? 'cursor-not-allowed text-muted-foreground'
                : 'text-primary hover:text-brand-deep hover:underline',
            )}
          >
            {resendIn > 0 ? `Resend in ${resendIn}s` : 'Send a new code'}
          </button>
        </div>

        <CtaButton
          busy={busy}
          disabled={expired}
          label={busy ? 'Verifying…' : 'Sign in'}
        />
      </form>

      <p className="mt-6 flex items-start gap-2 text-xs leading-relaxed text-[#6C6C6C]">
        <ShieldCheck className="mt-0.5 size-3.5" aria-hidden />
        You are not signed in yet. Nothing was stored when you entered your password - closing
        this tab now leaves no session behind.
      </p>
    </>
  );
}

/**
 * The app's primary CTA: a 48pt pill filled left-to-right with #831DED → #4116D1,
 * lifted on a violet-tinted shadow rather than a grey one.
 *
 * The one place in this panel that uses a gradient. `tailwind.config.ts` bans
 * them in the console deliberately, and that rule holds there - this screen is
 * the mobile app's, and the CTA gradient is the single most recognisable element
 * on it. Written as an inline style rather than a Tailwind utility so it cannot
 * be mistaken for permission to reach for one elsewhere.
 */
function CtaButton({
  busy,
  disabled = false,
  label,
}: {
  busy: boolean;
  disabled?: boolean;
  label: string;
}): JSX.Element {
  const dimmed = busy || disabled;

  return (
    <button
      type="submit"
      disabled={dimmed}
      className={cn(
        'mt-2 flex h-12 w-full items-center justify-center gap-2 rounded-xl text-base font-semibold text-white',
        'transition-[opacity,box-shadow,transform] duration-150 active:translate-y-px',
        'focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-[#634FFD] focus-visible:ring-offset-2',
        dimmed
          ? 'opacity-60'
          : 'shadow-[0_8px_16px_rgba(69,0,152,0.28)] hover:brightness-105',
      )}
      style={{ backgroundImage: 'linear-gradient(90deg, #831DED 0%, #4116D1 100%)' }}
    >
      {busy ? <Loader2 className="size-4 animate-spin" aria-hidden /> : null}
      <span className="truncate">{label}</span>
      {busy ? null : <ArrowRight className="size-4" aria-hidden />}
    </button>
  );
}

/**
 * Password reset.
 *
 * Uses Firebase's own reset email - delivered by Firebase from the template in
 * Console → Authentication → Templates, with no SMTP server or mail provider
 * needed anywhere.
 *
 * The confirmation is deliberately the same whether or not the address exists.
 * Saying "no account found" would turn this form into an account-enumeration
 * oracle: anyone could map which addresses hold staff accounts by watching which
 * ones error.
 */
function ResetForm({ onBack }: { onBack: () => void }): JSX.Element {
  const { sendPasswordReset } = useAuth();
  const [sentTo, setSentTo] = useState<string | null>(null);
  const [failure, setFailure] = useState<string | null>(null);

  const {
    register,
    handleSubmit,
    formState: { errors, isSubmitting },
  } = useForm<ResetValues>({
    resolver: zodResolver(resetSchema),
    defaultValues: { email: '' },
  });

  const onSubmit = handleSubmit(async (values) => {
    setFailure(null);
    try {
      await sendPasswordReset(values.email);
      setSentTo(values.email.trim());
    } catch (resetError) {
      setFailure(
        resetError instanceof Error ? resetError.message : 'Could not send the email. Try again.',
      );
    }
  });

  if (sentTo) {
    return (
      <div>
        <div className="mb-4 flex size-11 items-center justify-center rounded-lg bg-success-soft">
          <CheckCircle2 className="size-5 text-success" aria-hidden />
        </div>

        <p className="text-lg font-semibold leading-tight text-foreground">Check your inbox</p>
        <p className="mt-2 text-sm leading-relaxed text-[#434343]">
          If <span className="font-medium text-foreground">{sentTo}</span> has a staff account, a
          password-reset link is on its way. It expires in an hour.
        </p>
        <p className="mt-2 text-sm leading-relaxed text-[#6C6C6C]">
          Not there? Check spam, then ask a super admin to confirm the address on your account.
        </p>

        <Button variant="outline" className="mt-6 h-11 w-full rounded-xl" onClick={onBack}>
          <ArrowLeft aria-hidden />
          Back to sign in
        </Button>
      </div>
    );
  }

  return (
    <>
      <p className="mb-5 text-sm leading-relaxed text-[#434343]">
        We will email you a link to set a new password.
      </p>

      {failure ? (
        <div
          role="alert"
          className="mb-5 flex items-start gap-3 rounded-lg border border-destructive/25 bg-danger-soft p-3.5"
        >
          <AlertCircle className="mt-0.5 size-4 shrink-0 text-destructive" aria-hidden />
          <p className="text-sm leading-relaxed text-destructive">{failure}</p>
        </div>
      ) : null}

      <form onSubmit={onSubmit} noValidate className="flex flex-col gap-4">
        <Field
          id="reset-email"
          label="Work email"
          icon={<Mail className="size-4" aria-hidden />}
          error={errors.email?.message}
        >
          <Input
            id="reset-email"
            type="email"
            autoComplete="username"
            autoFocus
            placeholder="you@sect.fit"
            disabled={isSubmitting}
            aria-invalid={Boolean(errors.email)}
            className="h-12 rounded-xl border-[rgba(220,220,220,0.8)] pl-9 shadow-[0_4px_6px_-1px_rgba(0,0,0,0.06)]"
            {...register('email')}
          />
        </Field>

        <CtaButton busy={isSubmitting} label={isSubmitting ? 'Sending…' : 'Send reset link'} />

        <Button type="button" variant="ghost" className="h-10 w-full" onClick={onBack}>
          <ArrowLeft aria-hidden />
          Back to sign in
        </Button>
      </form>
    </>
  );
}

/** Label + optional trailing action + icon-prefixed control + error. */
function Field({
  id,
  label,
  icon,
  error,
  action,
  children,
}: {
  id: string;
  label: string;
  icon: React.ReactNode;
  error?: string;
  action?: React.ReactNode;
  children: React.ReactNode;
}): JSX.Element {
  return (
    <div className="flex flex-col gap-1.5">
      <div className="flex items-baseline justify-between gap-3">
        <Label htmlFor={id} className="text-sm font-semibold text-[#111111]">
          {label}
        </Label>
        {action}
      </div>

      <div className="relative">
        <span
          className="pointer-events-none absolute left-3 top-1/2 -translate-y-1/2 text-[#767676]"
          aria-hidden
        >
          {icon}
        </span>
        {children}
      </div>

      {error ? (
        <p id={`${id}-error`} className="text-sm font-medium text-destructive">
          {error}
        </p>
      ) : null}
    </div>
  );
}
